/* here&found — BONES use-illustration (assets/hf-mechanism-bones.js).
   08 §5: "bones · dances · hangs dead still → hand grabs ring → pull →
   4 wild dance frames (170ms, alternating limb sets, amber 'clatter!') →
   snaps back still."

   Drawn against the real product photo (illo-ref/bones.jpg): a chibi
   pull-string skeleton charm — the skull is a full third of the body, with
   two big black eye sockets and a band of vertical grin teeth; a white
   carabiner clip at the top joined to the skull by a COBALT cord (the
   product's own accent); a ribbed torso; and pin-jointed two-segment arms
   and legs with visible rivet joints. Skull:torso:legs ≈ 1:1:1.2.

   Night scene (08 §2): stroke = cream on the dark stage; the skull's
   features are ink on its cream fill, exactly like the photo. Cobalt
   appears once — the pull cord. Amber appears only as the spec's own
   "clatter!" (mandated by §5 for this scene). Poses hard-cut; the four
   dance frames run 170ms each with alternating limb sets. One charm: the
   moon and its halo. */

const K = window.HFIllo.kit;
const { q, Sh, Spark, Hand, st, INK, CREAM, CREAM_STROKE, AMBER } = K;
const COBALT = (getComputedStyle(document.documentElement).getPropertyValue('--accent-deep') || '#1B54D6').trim();
const C = CREAM_STROKE;

const FLOOR_Y = 276;

function room() {
  return `
    <!-- floor -->
    <path ${st(C, 2.6)} d="${q(48, FLOOR_Y, 484, FLOOR_Y - 4, 2.5)}"/>
    <!-- charm: moon + halo -->
    <circle cx="92" cy="64" r="13" fill="none" stroke="${C}" stroke-width="2.2"/>
    <circle cx="92" cy="64" r="21" fill="none" stroke="${C}" stroke-width="1.2" opacity="0.35"/>
    <!-- the rail and peg he hangs from, and his floor shadow -->
    <path ${st(C, 2.4)} d="${q(196, 44, 324, 41, 2)}"/>
    <circle cx="258" cy="48" r="3" fill="${CREAM}"/>
    ${Sh(257, FLOOR_Y - 4, 28, 5, 0.08)}
    <!-- the white carabiner clip, hooked on the peg -->
    <ellipse cx="258" cy="66" rx="8.5" ry="11.5" fill="none" stroke="${C}" stroke-width="2.4"/>
    <path ${st(C, 1.6)} d="${q(263, 74, 265, 66, 1.5)}"/>`;
}

/* the cobalt pull cord: clip bottom to skull top. Taut when pulled. */
const cord = (dy = 0, bow = 2.5) => `<path ${st(COBALT, 2.6)} d="${q(258, 77, 257, 98 + dy, bow)}"/>`;

/* a pin joint rivet */
const J = (x, y, r = 2.4) => `<circle cx="${x}" cy="${y}" r="${r}" fill="${CREAM}"/>`;

/* a two-segment limb: joint circle at the elbow/knee, bone-blob at the end */
const limb = ([x1, y1, x2, y2, x3, y3]) =>
  `<path ${st(C, 2.4)} d="${q(x1, y1, x2, y2, 2)} ${q(x2, y2, x3, y3, 2)}"/>${J(x2, y2)}
   <circle cx="${x3}" cy="${y3}" r="3.4" fill="${CREAM}" stroke="${C}" stroke-width="1.5"/>`;

/* the skeleton. rot swings the whole charm about the cord anchor (257,98);
   dy is pull travel; la/ra/ll/rl are the limb segment endpoints; rc is
   where the pull ring hangs. */
function skeleton({ rot = 0, dy = 0, la, ra, ll, rl, rc }) {
  return `<g transform="translate(0 ${dy}) rotate(${rot} 257 98)">
    <!-- skull: a third of him, big ink sockets, vertical grin teeth -->
    <circle cx="257" cy="119" r="21" fill="${CREAM}" stroke="${C}" stroke-width="2.6"/>
    <circle cx="249" cy="114" r="5" fill="${INK}"/>
    <circle cx="266" cy="114" r="5" fill="${INK}"/>
    <path ${st(INK, 1.4)} d="${q(245, 128, 269, 127, 1.5)} ${q(246, 134, 268, 133, 1.5)} ${q(249, 128, 249, 133, 0.4)} ${q(253, 128, 253, 134, 0.4)} ${q(257, 127, 257, 133, 0.4)} ${q(261, 128, 261, 133, 0.4)} ${q(265, 127, 265, 133, 0.4)}"/>
    ${J(257, 141, 2.6)}
    <!-- spine, three rib bars, pelvis bar -->
    <path ${st(C, 2.4)} d="${q(257, 140, 256, 171, 1.5)}"/>
    <path ${st(C, 2)} d="${q(244, 148, 270, 147, 1.5)} ${q(246, 156, 268, 155, 1.5)} ${q(248, 163, 266, 162, 1.5)}"/>
    <path ${st(C, 2.2)} d="${q(247, 170, 267, 169, 1.5)}"/>
    <!-- rivet joints at shoulders and hips -->
    ${J(245, 143, 2.8)} ${J(269, 143, 2.8)} ${J(250, 171, 2.8)} ${J(264, 171, 2.8)}
    ${limb(la)} ${limb(ra)} ${limb(ll)} ${limb(rl)}
    <!-- the pull ring on its short string -->
    <path ${st(C, 1.8)} d="${q(260, 172, rc[0], rc[1], 3)}"/>
    <circle cx="${rc[0] + 2}" cy="${rc[1] + 4}" r="5" fill="none" stroke="${C}" stroke-width="2"/>
  </g>`;
}

/* the grabbing hand, mirrored so the forearm comes in from the right */
const handR = (x, y, rot = 0) =>
  `<g transform="translate(${2 * x} 0) scale(-1 1)">${Hand(x, y, rot, C)}</g>`;

const clatter = () => `<text x="316" y="108" fill="${AMBER}"
  font-family="ui-monospace, SFMono-Regular, Menlo, monospace" font-size="14" letter-spacing="1">clatter!</text>`;

/* limb sets. hang = everything dead straight down. */
const HANG = {
  la: [245, 143, 242, 158, 241, 173], ra: [269, 143, 272, 158, 273, 173],
  ll: [250, 171, 249, 193, 248, 214], rl: [264, 171, 265, 193, 266, 214],
};
const TENSE = {
  la: [245, 143, 240, 152, 236, 162], ra: [269, 143, 274, 152, 278, 162],
  ll: [250, 171, 246, 186, 247, 200], rl: [264, 171, 268, 186, 267, 200],
};
/* the four wild frames — alternating limb sets, per §5 */
const DANCE = [
  { rot: -7, la: [245, 143, 234, 130, 228, 114], ra: [269, 143, 280, 158, 290, 168],
    ll: [250, 171, 238, 186, 230, 200], rl: [264, 171, 267, 193, 268, 214], rc: [262, 196] },
  { rot: 7,  la: [245, 143, 234, 158, 224, 168], ra: [269, 143, 280, 130, 286, 114],
    ll: [250, 171, 247, 193, 246, 214], rl: [264, 171, 276, 186, 284, 200], rc: [286, 194] },
  { rot: -4, la: [245, 143, 230, 134, 220, 120], ra: [269, 143, 284, 134, 294, 120],
    ll: [250, 171, 236, 184, 226, 196], rl: [264, 171, 278, 184, 288, 196], rc: [270, 200] },
  { rot: 5,  la: [245, 143, 230, 146, 216, 142], ra: [269, 143, 284, 146, 298, 142],
    ll: [250, 171, 252, 190, 244, 206], rl: [264, 171, 262, 190, 270, 206], rc: [280, 190] },
];
const dance = (i, sparkX) =>
  room() + cord(0, 1.5) + skeleton({ ...DANCE[i] }) + clatter() + Spark(sparkX, 126, C, 0.9);

const POSES = {
  'still':   () => room() + cord() + skeleton({ ...HANG, rc: [272, 192] }),
  'grab':    () => room() + cord() + skeleton({ ...HANG, rc: [272, 192] }) + handR(282, 202, -12),
  'pull':    () => room() + cord(7, 0.5) + skeleton({ ...TENSE, dy: 7, rc: [276, 215] }) + handR(286, 226, -8),
  'dance-a': () => dance(0, 212),
  'dance-b': () => dance(1, 302),
  'dance-c': () => dance(2, 208),
  'dance-d': () => dance(3, 306),
  'snap':    () => room() + cord() + skeleton({ ...HANG, rc: [272, 192] }),
};

window.HFIllo.register('bones', {
  label: 'A hand pulls the ring and the little skeleton charm rattles through a wild dance, then snaps back still',
  night: true,
  settled: 'snap',
  phases: [['still', 950], ['grab', 650], ['pull', 430], ['dance-a', 170], ['dance-b', 170],
           ['dance-c', 170], ['dance-d', 170], ['snap', 1800]],
  tags: {
    'still': 'Dead still',
    'grab': 'Grab the ring',
    'pull': 'Pull —',
    'dance-a': 'Total rattle',
    'dance-b': 'Total rattle',
    'dance-c': 'Total rattle',
    'dance-d': 'Total rattle',
    'snap': 'Back to still',
  },
  draw: (name) => (POSES[name] || POSES['snap'])(),
});

window.HFIllo.define();
